
import React, { useState, useEffect } from 'react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Lock, Loader2, Key } from 'lucide-react';
import PasswordError from './PasswordError';

interface MainLoginFormProps {
  onSubmit: (password: string) => void;
  error: string;
  remainingAttempts: number;
  isLoading: boolean;
  onShowResetForm: () => void;
  onShowPassKeyForm: () => void;
}

const MainLoginForm = ({ 
  onSubmit, 
  error, 
  remainingAttempts,
  isLoading,
  onShowResetForm,
  onShowPassKeyForm
}: MainLoginFormProps) => {
  const [password, setPassword] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!password.trim()) return;
    onSubmit(password);
  };

  // Clear password when error changes (i.e., after a failed attempt)
  useEffect(() => {
    if (error) {
      setPassword('');
    }
  }, [error]);

  // Clear password when component mounts
  useEffect(() => {
    setPassword('');
  }, []);

  return (
    <>
      <div className="text-center space-y-4 mb-8 relative z-10">
        <div className="bg-slate-800/40 p-4 rounded-full inline-flex items-center justify-center">
          <Lock className="h-10 w-10 text-cyan-400 cyber-icon" />
        </div>
        <h2 className="text-2xl font-bold cyber-text-glow">Password Required</h2>
        <p className="text-cyan-100 opacity-80 text-left">
          Please enter your password to unlock the application
        </p>
      </div>
      
      <form onSubmit={handleSubmit} className="space-y-6 relative z-10" autoComplete="off">
        <div className="space-y-3">
          <Label htmlFor="password" className="text-base font-medium text-cyan-100">Password</Label>
          <Input
            id="password"
            name="password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoFocus
            autoComplete="new-password" 
            className="w-full text-lg h-12 px-4 cyber-input text-left"
            dir="ltr"
            placeholder="Enter your password"
            disabled={isLoading}
            required
            spellCheck={false}
          />
          <PasswordError errorMessage={error} remainingAttempts={remainingAttempts} />
        </div>
        
        <Button 
          type="submit" 
          className="w-full py-6 text-base font-medium cyber-button"
          disabled={isLoading || !password.trim()}
        >
          {isLoading ? (
            <span className="flex items-center justify-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" />
              Verifying...
            </span>
          ) : (
            "Unlock"
          )}
        </Button>
        
        <div className="flex justify-between pt-4 text-sm">
          <Button 
            type="button" 
            variant="link" 
            className="text-cyan-300 hover:text-cyan-100"
            onClick={onShowResetForm}
          >
            Reset Password
          </Button>
          
          <Button 
            type="button" 
            variant="link" 
            className="text-amber-300 hover:text-amber-100 flex items-center gap-1"
            onClick={onShowPassKeyForm}
          >
            <Key className="h-3.5 w-3.5" />
            VIP Pass Key
          </Button>
        </div>
      </form>
    </>
  );
};

export default MainLoginForm;
